import type {Directive} from 'vue'
import {Capacitor} from '@capacitor/core'
import {Keyboard} from '@capacitor/keyboard'

const DEBUG = false
const EXTRA_PADDING = 16
const isIOSNative = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
const INPUT_SELECTORS = 'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="submit"]):not([type="button"]), textarea, [contenteditable]'

const isMobilePlatform = Capacitor.isNativePlatform() || (typeof navigator !== 'undefined' && /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))

interface ScrollOptions {
	offset?: number
	behavior?: 'auto' | 'smooth'
	center?: boolean
}

function scrollToInput(el: HTMLElement, keyboardHeight = 0, options: ScrollOptions = {}) {
	el.scrollIntoView({
		behavior: options.behavior || 'smooth',
		block: options.center ? 'center' : 'nearest'
	})
}

export const vScrollIntoView: Directive<HTMLElement, ScrollOptions> = {
	mounted(el, binding) {
		const focusListener = () => {
			scrollToInput(el, 0, binding.value || {})
		}
		const blurListener = () => {}

		el.addEventListener('focus', focusListener)
		el.addEventListener('blur', blurListener)

		;(el as any)._focusListener = focusListener
		;(el as any)._blurListener = blurListener
	},
	updated(el, binding) {
		if (document.activeElement === el) {
			requestAnimationFrame(() => {
				scrollToInput(el, 0, binding.value || {})
			})
		}
	},
	unmounted(el) {
		if ((el as any)._focusListener) {
			el.removeEventListener('focus', (el as any)._focusListener)
		}
		if ((el as any)._blurListener) {
			el.removeEventListener('blur', (el as any)._blurListener)
		}
	}
}

let keyboardListenerRegistered = false

export function registerKeyboardScrollListener() {
	if (keyboardListenerRegistered) return
	keyboardListenerRegistered = true

	// Mark root element with class on iOS native
	if (isIOSNative) {
		document.documentElement.classList.add('is-ios-native')
	}

	// Register global scroll centering for focused inputs on mobile platforms (both native apps and mobile web browsers)
	if (isMobilePlatform) {
		document.addEventListener('focusin', (e) => {
			const target = e.target as HTMLElement
			if (target && target.matches && target.matches(INPUT_SELECTORS)) {
				setTimeout(() => {
					if (document.activeElement === target) {
						const keyboardHeight = isIOSNative ? parseInt(document.documentElement.style.getPropertyValue('--keyboard-height') || '0', 10) : 0
						scrollToInput(target, keyboardHeight, { center: true })
					}
				}, 150)
			}
		})

		// Android Native only: scroll active input into view after keyboard fully shows and layout resize completes
		if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
			try {
				Keyboard.addListener('keyboardDidShow', () => {
					const target = document.activeElement as HTMLElement | null
					if (target && target.matches(INPUT_SELECTORS)) {
						scrollToInput(target, 0, { center: true })
					}
				})
			} catch (err) {
				console.warn('[Keyboard] Failed to register Android didShow listener:', err)
			}
		}
	}

	// Viewport resizing/shrinking adjustments are strictly iOS native only
	if (!isIOSNative) return

	try {
		const showHandler = (info: { keyboardHeight: number }) => {
			if (DEBUG) console.log('keyboard show', info.keyboardHeight)
			const heightVal = window.innerHeight - info.keyboardHeight

			document.documentElement.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`)
			document.documentElement.style.setProperty('--visual-viewport-height', `${heightVal}px`)
			document.documentElement.classList.add('keyboard-open')

			const target = document.activeElement as HTMLElement | null
			if (target && target.matches(INPUT_SELECTORS)) {
				// Center immediately
				scrollToInput(target, info.keyboardHeight, { center: true })
				// Center again after CSS transition (0.28s) completes to guarantee precise alignment
				setTimeout(() => {
					if (document.activeElement === target) {
						scrollToInput(target, info.keyboardHeight, { center: true })
					}
				}, 300)
			}
		}

		const hideHandler = () => {
			if (DEBUG) console.log('keyboard hide')
			document.documentElement.style.setProperty('--keyboard-height', '0px')
			document.documentElement.style.removeProperty('--visual-viewport-height')
			document.documentElement.classList.remove('keyboard-open')
		}

		Keyboard.addListener('keyboardWillShow', showHandler)
		Keyboard.addListener('keyboardWillHide', hideHandler)
	} catch (err) {
		console.warn('[Keyboard] Failed to register iOS native keyboard listeners:', err)
	}
}